export type Appearance = 'system' | 'light' | 'dark';
export type Accent = 'blue' | 'green' | 'violet';
export type Contrast = 'standard' | 'high';

export interface ThemeState {
  appearance: Appearance;
  accent: Accent;
  contrast: Contrast;
}

export const DEFAULT_THEME: ThemeState = {
  appearance: 'system',
  accent: 'blue',
  contrast: 'standard',
};

/**
 * Storage keys. `sp.theme` is the legacy appearance-only key kept readable so an
 * upgrade never resets the cashier's light/dark choice; the three scoped keys
 * carry the separated appearance/accent/contrast state going forward.
 */
export const LEGACY_THEME_KEY = 'sp.theme';
export const THEME_STORAGE_KEYS = {
  appearance: 'sp.theme.appearance',
  accent: 'sp.theme.accent',
  contrast: 'sp.theme.contrast',
} as const;

export function parseAppearance(raw: unknown): Appearance {
  return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'system';
}

export function parseAccent(raw: unknown): Accent {
  return raw === 'green' || raw === 'violet' || raw === 'blue' ? raw : 'blue';
}

export function parseContrast(raw: unknown): Contrast {
  return raw === 'high' ? 'high' : 'standard';
}

type ReadableStore = Pick<Storage, 'getItem'>;
type WritableStore = Pick<Storage, 'getItem' | 'setItem'>;

function safeGet(store: ReadableStore, key: string): string | null {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Read the terminal/user preference. Invalid values are dropped (never applied),
 * and blocked storage reads as "no preference" so the till still renders.
 */
export function readStoredTheme(store: ReadableStore): Partial<ThemeState> {
  const stored: Partial<ThemeState> = {};
  const appearance = safeGet(store, THEME_STORAGE_KEYS.appearance) ?? safeGet(store, LEGACY_THEME_KEY);
  if (appearance !== null && (appearance === 'light' || appearance === 'dark' || appearance === 'system')) {
    stored.appearance = appearance;
  }
  const accent = safeGet(store, THEME_STORAGE_KEYS.accent);
  if (accent === 'blue' || accent === 'green' || accent === 'violet') stored.accent = accent;
  if (safeGet(store, THEME_STORAGE_KEYS.contrast) === 'high') stored.contrast = 'high';
  else if (safeGet(store, THEME_STORAGE_KEYS.contrast) === 'standard') stored.contrast = 'standard';
  return stored;
}

/**
 * Precedence: stored terminal/user choice first, then the store default pushed
 * by the server (once /pos/config exposes ui.default_*), then built-ins.
 */
export function resolveTheme(options: {
  stored: Partial<ThemeState>;
  storeDefaults?: Partial<ThemeState>;
  fallback?: ThemeState;
}): ThemeState {
  const base = options.fallback ?? DEFAULT_THEME;
  const defaults = options.storeDefaults ?? {};
  return {
    appearance: options.stored.appearance ?? defaults.appearance ?? base.appearance,
    accent: options.stored.accent ?? defaults.accent ?? base.accent,
    contrast: options.stored.contrast ?? defaults.contrast ?? base.contrast,
  };
}

/** Blocked-storage safe: a denied write keeps the in-memory choice, never throws. */
export function persistTheme(store: WritableStore, state: ThemeState): void {
  for (const [key, value] of [
    [THEME_STORAGE_KEYS.appearance, state.appearance],
    [THEME_STORAGE_KEYS.accent, state.accent],
    [THEME_STORAGE_KEYS.contrast, state.contrast],
  ] as const) {
    try {
      store.setItem(key, value);
    } catch {
      // storage blocked: the in-memory + DOM choice still applies this session
    }
  }
}

/** Minimal document surface so node tests can use a stub; the app passes document.documentElement. */
export interface ThemeRoot {
  setAttribute(name: string, value: string): void;
  removeAttribute(name: string): void;
}

/**
 * Apply without reload: only root attributes change, so cart, payment draft,
 * scan queue and pending server attempts are untouched. Blue (default) and
 * standard contrast are encoded as ABSENT attributes, keeping the default
 * render identical to before this feature existed.
 */
export function applyThemeToRoot(root: ThemeRoot, state: ThemeState): void {
  if (state.appearance === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', state.appearance);
  if (state.accent === 'blue') root.removeAttribute('data-accent');
  else root.setAttribute('data-accent', state.accent);
  if (state.contrast === 'standard') root.removeAttribute('data-contrast');
  else root.setAttribute('data-contrast', state.contrast);
}

/**
 * Boot/boot-retry entry: read, resolve, apply. Never throws and never touches
 * anything except the root element's theme attributes.
 */
export function applyStoredThemeFull(store: ReadableStore, root: ThemeRoot, storeDefaults?: Partial<ThemeState>): ThemeState {
  let state: ThemeState;
  try {
    state = resolveTheme({ stored: readStoredTheme(store), storeDefaults });
  } catch {
    state = { ...DEFAULT_THEME };
  }
  try {
    applyThemeToRoot(root, state);
  } catch {
    // a hostile root must not break boot
  }
  return state;
}
